
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { load } from '@cashfreepayments/cashfree-js';
import '../styles/MarketingTheme.css';


import MarketingHeader from '../components/MarketingHeader';

const PricingPage = () => {
    const navigate = useNavigate();
    // const { t } = useLanguage();
    const { token, isLoading: authLoading } = useAuth();
    const [currency, setCurrency] = useState('INR');
    const [billingPeriod, setBillingPeriod] = useState('yearly'); // 'halfYearly' or 'yearly'
    const [loading, setLoading] = useState(false);
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [branchCount, setBranchCount] = useState(1);
    const [pendingPlan, setPendingPlan] = useState(null);
    const [couponCode, setCouponCode] = useState('');
    const [showCouponInput, setShowCouponInput] = useState(false);
    const [couponPlan, setCouponPlan] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponValidation, setCouponValidation] = useState(null);
    const [couponBranchCount, setCouponBranchCount] = useState(1);

    const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:5000'
        : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

    // Currency metadata for symbols and formatting
    const currencyMeta = {
        INR: { symbol: '₹', locale: 'en-IN' },
        USD: { symbol: '$', locale: 'en-US' },
        EUR: { symbol: '€', locale: 'de-DE' },
        GBP: { symbol: '£', locale: 'en-GB' },
        AED: { symbol: 'د.إ', locale: 'ar-AE' },
        SAR: { symbol: '﷼', locale: 'ar-SA' },
        SGD: { symbol: 'S$', locale: 'en-SG' },
        AUD: { symbol: 'A$', locale: 'en-AU' },
        CAD: { symbol: 'C$', locale: 'en-CA' }
    };

    // Auto-detect location/currency
    useEffect(() => {
        try {
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (userTimeZone === 'Asia/Calcutta' || userTimeZone === 'Asia/Kolkata') {
                setCurrency('INR');
            } else if (userTimeZone.includes('Europe')) {
                setCurrency('EUR');
            } else if (userTimeZone.includes('London')) {
                setCurrency('GBP');
            } else if (userTimeZone.includes('Dubai') || userTimeZone.includes('Dubai')) {
                setCurrency('AED');
            } else {
                setCurrency('USD');
            }
        } catch (e) {
            console.error("Timezone detection failed", e);
            setCurrency('USD');
        }
    }, []);

    const plans = {
        starter: {
            name: 'Starter',
            priceMonthly: {
                INR: 399, USD: 9, EUR: 8, GBP: 7, AED: 33, SAR: 34, SGD: 12, AUD: 14, CAD: 12
            },
            priceHalfYearly: {
                INR: 2394, USD: 49, EUR: 45, GBP: 39, AED: 180, SAR: 185, SGD: 66, AUD: 75, CAD: 66
            },
            priceYearly: {
                INR: 4788, USD: 84, EUR: 78, GBP: 66, AED: 310, SAR: 315, SGD: 114, AUD: 128, CAD: 114
            },
            features: [
                'Single Branch Management',
                'Up to 120 Menu Items',
                'Up to 20 Tables',
                'Menu Item Add-ons',
                'Live Kitchen Display System (KDS)',
                'Basic Analytics (Day/Week)',
                'Staff Management (Add/Update)',
                'Online Payments (UPI, Cards)',
                'Standard Email Support',
                'Language Support: English Only (Telugu, Hindi, Tamil - Coming Soon)'
            ],
            recommended: false
        },
        premium: {
            name: 'Premium',
            priceMonthly: {
                INR: 799, USD: 19, EUR: 18, GBP: 15, AED: 70, SAR: 72, SGD: 26, AUD: 29, CAD: 26
            },
            priceHalfYearly: {
                INR: 4794, USD: 103, EUR: 95, GBP: 82, AED: 380, SAR: 388, SGD: 139, AUD: 157, CAD: 139
            },
            priceYearly: {
                INR: 9588, USD: 180, EUR: 165, GBP: 142, AED: 660, SAR: 675, SGD: 243, AUD: 275, CAD: 243
            },
            features: [
                'All Starter Features Included',
                'Multi-Branch Management',
                'AI Nutritional Info Generation',
                'Bulk Menu Upload/Download (Excel)',
                'Ingredients & Inventory Management',
                'Rewards System & Loyalty Program',
                'Manual Ordering (Swiggy, Zomato, offline)',
                'Staff Attendance, Leaves & Salary',
                'Advanced Analytics & Downloads',
                'Custom Roles & Permissions',
                'Priority WhatsApp & Mail Support',
                'Swiggy, Zomato & Other Integrations (Coming Soon)'
            ],
            recommended: true
        }
    };

    const getEffectiveMonthlyPrice = (plan) => {
        const total = billingPeriod === 'halfYearly' ? plan.priceHalfYearly[currency] : plan.priceYearly[currency];
        const months = billingPeriod === 'halfYearly' ? 6 : 12;
        return Math.round(total / months);
    };

    const formatPrice = (amount) => {
        const meta = currencyMeta[currency] || currencyMeta.USD;
        return `${meta.symbol}${amount.toLocaleString(meta.locale)}`;
    };

    const handlePayment = async (planType, amount, branchCountSet = false) => {
        // Wait for auth to finish loading before checking token
        const effectiveToken = token || localStorage.getItem('token');
        if (!authLoading && !effectiveToken) {
            navigate('/signup');
            return;
        }

        if (planType === 'premium' && !branchCountSet) {
            setPendingPlan({ planType, amount });
            setShowBranchModal(true);
            return;
        }

        const finalBranchLimit = planType === 'premium' ? branchCount : 1;
        const baseAmount = amount * finalBranchLimit;

        // Apply coupon discount if validated for this plan
        const finalAmount = (couponValidation && couponPlan === planType)
            ? couponValidation.discounted_amount
            : baseAmount;

        setLoading(true);

        try {
            // 1. Create Order on Backend
            const res = await fetch(`${API_URL}/api/payment/create-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${effectiveToken}`
                },
                body: JSON.stringify({ 
                    plan_type: planType, 
                    amount: finalAmount,
                    currency: currency,
                    branch_limit: finalBranchLimit,
                    billing_period: billingPeriod,
                    coupon_code: couponCode.trim() || undefined
                })
            });

            const data = await res.json();

            if (!data.success) {
                throw new Error(data.message || 'Failed to create order');
            }

            // 2. Initiate Checkout based on Gateway
            if (data.gateway === 'razorpay') {
                console.log('[Payment] Opening Razorpay Checkout...');
                const options = {
                    key: data.razorpay_key,
                    amount: data.amount,
                    currency: data.currency,
                    name: "RedSorm",
                    description: `${planType.toUpperCase()} Plan Subscription`,
                    order_id: data.razorpay_order_id,
                    handler: async function (response) {
                        setLoading(true);
                        try {
                            const verifyRes = await fetch(`${API_URL}/api/payment/verify`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${effectiveToken}`
                                },
                                body: JSON.stringify({
                                    order_id: data.order_id,
                                    razorpay_payment_id: response.razorpay_payment_id,
                                    razorpay_order_id: response.razorpay_order_id,
                                    razorpay_signature: response.razorpay_signature
                                })
                            });
                            const verifyData = await verifyRes.json();
                            if (verifyData.success) {
                                window.location.href = `/payment/status?order_id=${data.order_id}`;
                            } else {
                                throw new Error(verifyData.message || 'Payment verification failed');
                            }
                        } catch (vErr) {
                            alert(vErr.message);
                            setLoading(false);
                        }
                    },
                    prefill: {
                        name: data.customer_name,
                        email: data.customer_email,
                        contact: data.customer_phone
                    },
                    theme: { color: "#4f46e5" },
                    modal: {
                        ondismiss: function() {
                            setLoading(false);
                        }
                    }
                };
                const rzp = new window.Razorpay(options);
                rzp.open();
            } else {
                // FALLBACK TO CASHFREE
                console.log('[Payment] Opening Cashfree Checkout...');
                const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
                const cashfree = await load({
                    mode: isProduction ? "production" : "sandbox"
                });

                const checkoutOptions = {
                    paymentSessionId: data.payment_session_id,
                    redirectTarget: "_self"
                };
                cashfree.checkout(checkoutOptions);
            }

        } catch (err) {
            console.error("Payment Error:", err);
            setLoading(false);
            alert(err.message || 'Something went wrong during payment initialization.');
        }
    };

    const comparisonCategories = [
        {
            category: "Admin Management",
            items: [
                { name: "Menu Management", starter: "120 Items", premium: "Unlimited", note: "Categories Included" },
                { name: "Table Management", starter: "20 Tables", premium: "Unlimited", note: "Table Groups Included" },
                { name: "Branch Management", starter: "Single Branch", premium: "Multi-Branch", highlight: true },
                { name: "AI Nutrition Info Generation", starter: true, premium: true },
                { name: "Bulk Item Upload/Download", starter: false, premium: true },
                { name: "Self-Service Management", starter: true, premium: true },
                { name: "Rewards & Loyalty System", starter: false, premium: true, note: "Customer points, custom redemption rules & birthday tracking" },
            ]
        },
        {
            category: "Free Services",
            items: [
                { name: "Any Customizations", starter: false, premium: true, note: "Premium only - Toppings, Sizes, Options, Variations" },
                { name: "Free Demo", starter: true, premium: true, note: "Live 1-on-1 walkthrough of the entire system" },
                { name: "Free Setup", starter: true, premium: true, note: "Restaurant account onboarding & configuration" },
                { name: "Free Menu Import", starter: true, premium: true, note: "Send us your menu and we'll upload/design it for you" }
            ]
        },
        {
            category: "Kitchen & Operations",
            items: [
                { name: "Live KDS (Instant Orders)", starter: true, premium: true },
                { name: "Order Status Updates", starter: true, premium: true },
                { name: "Active Orders Oversight", starter: true, premium: true },
                { name: "Preparation Time Tracking", starter: true, premium: true },
                { name: "Order History", starter: "24 Hours", premium: "Unlimited" },
                { name: "Manual Ordering & Entry", starter: false, premium: true, note: "Manually log Swiggy, Zomato, and phone orders in KDS" }
            ]
        },
        {
            category: "Analytics & Reports",
            // Analytics & Reports: first 3 available on Starter, next items require Premium
            items: [
                { name: "Overview Dashboard", starter: true, premium: true, note: "Quick summary of sales, orders and KPIs" },
                { name: "Revenue", starter: true, premium: true, note: "Sales by period, channels and trends" },
                { name: "Items", starter: true, premium: true, note: "Top selling items and item-wise performance" },
                { name: "Discounts & Promos", starter: false, premium: true, note: "Promo impact and discount summaries" },
                { name: "Operations", starter: false, premium: true, note: "Order time, prep time and operations KPIs" },
                { name: "Cancellations & Loss", starter: false, premium: true, note: "Track cancellations, refunds and losses" },
                { name: "Exportable Reports (Excel/PDF)", starter: false, premium: true, highlight: true, note: "Download detailed reports" },
                { name: "AI Suggestions", starter: false, premium: true, note: "AI-driven menu and pricing suggestions" }
            ]
        },
        {
            category: "Inventory & Ingredients",
            items: [
                { name: "Ingredient Tracking", starter: false, premium: true },
                { name: "Low Stock Alerts", starter: false, premium: true },
                { name: "Cost Management", starter: false, premium: true }
            ]
        },
        {
            category: "Staff & HR",
            items: [
                { name: "Staff Profiles", starter: true, premium: true },
                { name: "Management Roles", starter: "Basic", premium: "Custom & Advanced" },
                { name: "Attendance Tracking (In/Out)", starter: true, premium: true },
                { name: "Leave Management", starter: false, premium: true },
                { name: "Salary Reports", starter: false, premium: true }
            ]
        },
        {
            category: "Support & Extras",
            items: [
                { name: "Customer Page (Live)", starter: true, premium: true },
                { name: "Language Support", starter: "English Only (Telugu, Hindi, Tamil - Coming Soon)", premium: "English Only (Hindi, Spanish, French, Chinese, Tamil, Malayalam, Telugu - Coming Soon)" },
                { name: "Payment Integration", starter: true, premium: true },
                { name: "Support Channel", starter: "Email", premium: "WhatsApp + Email Priority" },
                { name: "Swiggy, Zomato & Other Integrations", starter: false, premium: "Coming Soon", highlight: true }
            ]
        }
    ];

    return (
        <div className="marketing-page stunning-theme">
            <MarketingHeader />

            {/* Pricing Section */}
            <section className="marketing-section marketing-section-hero pb-8">
                <div className="marketing-container text-center">
                    <div className="marketing-section-badge">Simple Pricing</div>
                    <h1 className="marketing-hero-title mb-6">
                        Invest in Your <span className="marketing-hero-gradient">Future</span>
                    </h1>

                    {/* Billing Period & Currency Toggle */}
                    <div className="flex flex-col md:flex-row justify-center items-center gap-6 mb-8">
                        {/* Billing Toggle */}
                        <div className="marketing-toggle-container bg-white/10 p-1 rounded-xl border border-white/10 flex">
                            <button
                                onClick={() => setBillingPeriod('halfYearly')}
                                className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${billingPeriod === 'halfYearly'
                                    ? 'bg-purple-600 text-white shadow-lg'
                                    : 'text-gray-400 hover:text-white'
                                    }`}
                            >
                                6 Months
                            </button>
                            <button
                                onClick={() => setBillingPeriod('yearly')}
                                className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${billingPeriod === 'yearly'
                                    ? 'bg-purple-600 text-white shadow-lg'
                                    : 'text-gray-400 hover:text-white'
                                    }`}
                            >
                                Yearly
                            </button>
                        </div>

                        {/* Currency Selector */}
                        <div className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-xl border border-white/10">
                            <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">Currency</span>
                            <select 
                                value={currency} 
                                onChange={async (e) => {
                                    const newCurr = e.target.value;
                                    setCurrency(newCurr);
                                    if (token) {
                                        try {
                                            await fetch(`${API_URL}/api/company/currency`, {
                                                method: 'PUT',
                                                headers: {
                                                    'Content-Type': 'application/json',
                                                    'Authorization': `Bearer ${token}`
                                                },
                                                body: JSON.stringify({ currency: newCurr })
                                            });
                                        } catch (err) {
                                            console.error('Failed to save currency preference');
                                        }
                                    }
                                }}
                                className="bg-transparent text-white font-bold outline-none cursor-pointer"
                            >
                                {Object.keys(currencyMeta).map(curr => (
                                    <option key={curr} value={curr} className="bg-slate-900">

                                        {curr} ({currencyMeta[curr].symbol})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* SPECIAL OFFER BANNER */}
                    <div className="max-w-4xl mx-auto mb-10 transform hover:scale-[1.01] transition-transform duration-300">
                        <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-3xl p-6 shadow-2xl border border-white/20 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
                            <div className="relative z-10">
                                <div className="inline-block bg-white/20 px-4 py-1 rounded-full text-white text-sm font-bold mb-2 backdrop-blur-sm">
                                    🎉 NEW YEAR 2026 SPECIAL
                                </div>
                                <h3 className="text-3xl md:text-4xl font-black text-white mb-1">
                                    20 DAYS FREE TRIAL
                                </h3>
                                <p className="text-white/80 text-sm">
                                    2 + 0 + 2 + 6 = 10 × 2 = 20 Days All-Access Premium Pass • No credit card required.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto text-left">
                        {/* Starter Plan */}
                        <div className="marketing-card flex flex-col h-full border-t-4 border-t-gray-500">
                            <div className="mb-6">
                                <h2 className="text-2xl font-bold text-white mb-2">{plans.starter.name}</h2>
                                <p className="text-gray-400">Perfect for small cafes & startups</p>
                            </div>
                            <div className="mb-8 p-6 bg-white/5 rounded-2xl border border-white/5">
                                <div className="flex items-baseline gap-1 mb-2">
                                    <span className="text-4xl font-bold text-white">
                                        {formatPrice(getEffectiveMonthlyPrice(plans.starter))}
                                    </span>
                                    <span className="text-gray-400">/mo/branch</span>
                                </div>
                                <div className="text-sm text-gray-400">
                                    Billed {billingPeriod === 'halfYearly' ? 'Half-Yearly' : 'Yearly'}: {formatPrice(billingPeriod === 'halfYearly' ? plans.starter.priceHalfYearly[currency] : plans.starter.priceYearly[currency])}
                                </div>
                            </div>

                            <ul className="marketing-list space-y-4 mb-8 flex-grow">
                                {plans.starter.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <i className="fas fa-check-circle text-purple-500 mt-1"></i>
                                        <span className="text-gray-300 text-sm">{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => {
                                    const amount = billingPeriod === 'halfYearly' ? plans.starter.priceHalfYearly[currency] : plans.starter.priceYearly[currency];
                                    handlePayment('starter', amount);
                                }}
                                disabled={loading}
                                className="marketing-btn marketing-btn-secondary w-full text-center justify-center"
                            >
                                {loading ? <i className="fas fa-spinner fa-spin"></i> : (token ? 'Select Starter' : 'Get Started')}
                            </button>
                            <div className="mt-4 text-xs text-center text-gray-400">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCouponInput(true);
                                        setCouponPlan('starter');
                                        setCouponError('');
                                        setCouponValidation(null);
                                    }}
                                    className="underline hover:text-white"
                                >
                                    Have a coupon code?
                                </button>
                            </div>
                        </div>

                        {/* Premium Plan */}
                        <div
                            className="marketing-card flex flex-col h-full border-t-4 border-t-orange-500 relative transform md:-translate-y-4"
                            style={{ overflow: 'visible', zIndex: 10 }}
                        >
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-orange-500 to-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg whitespace-nowrap z-20">
                                MOST POPULAR
                            </div>
                            <div className="mb-6">
                                <h2 className="text-2xl font-bold text-white mb-2">{plans.premium.name}</h2>
                                <p className="text-gray-400">For growing chains & serious venues</p>
                            </div>
                            <div className="mb-8 p-6 bg-gradient-to-br from-purple-900/40 to-orange-900/40 rounded-2xl border border-white/10">
                                <div className="flex items-baseline gap-1 mb-2">
                                    <span className="text-4xl font-bold text-white">
                                        {formatPrice(getEffectiveMonthlyPrice(plans.premium))}
                                    </span>
                                    <span className="text-gray-400">/mo/branch</span>
                                </div>
                                <div className="text-sm text-gray-400">
                                    Billed {billingPeriod === 'halfYearly' ? 'Half-Yearly' : 'Yearly'}: {formatPrice(billingPeriod === 'halfYearly' ? plans.premium.priceHalfYearly[currency] : plans.premium.priceYearly[currency])}
                                </div>
                            </div>

                            <ul className="marketing-list space-y-4 mb-8 flex-grow">
                                {plans.premium.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <i className="fas fa-check-circle text-orange-500 mt-1"></i>
                                        <span className="text-gray-300 text-sm">{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => {
                                    const amount = billingPeriod === 'halfYearly' ? plans.premium.priceHalfYearly[currency] : plans.premium.priceYearly[currency];
                                    handlePayment('premium', amount);
                                }}
                                disabled={loading}
                                className="marketing-btn marketing-btn-primary w-full text-center justify-center"
                            >
                                {loading ? <i className="fas fa-spinner fa-spin"></i> : (token ? 'Select Premium' : 'Start Free Trial')}
                            </button>
                            <div className="mt-4 text-xs text-center text-gray-400">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCouponInput(true);
                                        setCouponPlan('premium');
                                        setCouponError('');
                                        setCouponValidation(null);
                                    }}
                                    className="underline hover:text-white"
                                >
                                    Have a coupon code?
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {showCouponInput && (
                <section className="marketing-section marketing-section-hero pb-8">
                    <div className="marketing-container max-w-3xl">
                        <div className="marketing-card p-6 bg-white/5 border border-white/10 rounded-3xl text-white">
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-xl font-bold">Enter coupon code</h3>
                                        <p className="text-sm text-gray-400">
                                            {couponPlan === 'premium' ? 'Enter branches and coupon to see your discounted price.' : 'Enter your coupon code to get a discount.'}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCouponInput(false);
                                            setCouponError('');
                                            setCouponValidation(null);
                                            setCouponCode('');
                                        }}
                                        className="text-gray-400 hover:text-white"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>

                                {/* Branch count input for premium */}
                                {couponPlan === 'premium' && (
                                    <div>
                                        <label className="block text-gray-400 text-sm font-bold uppercase mb-2">Number of Branches</label>
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={() => { setCouponBranchCount(Math.max(1, couponBranchCount - 1)); setCouponValidation(null); }}
                                                className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 flex items-center justify-center"
                                            >
                                                <i className="fas fa-minus"></i>
                                            </button>
                                            <input
                                                type="number"
                                                min="1"
                                                value={couponBranchCount}
                                                onChange={(e) => { setCouponBranchCount(Math.max(1, parseInt(e.target.value) || 1)); setCouponValidation(null); }}
                                                className="w-20 h-10 bg-white/5 border border-white/10 rounded-xl text-center text-lg font-bold text-white outline-none focus:border-purple-500"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => { setCouponBranchCount(couponBranchCount + 1); setCouponValidation(null); }}
                                                className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 flex items-center justify-center"
                                            >
                                                <i className="fas fa-plus"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="grid gap-3 md:grid-cols-[1fr_auto] items-end">
                                    <input
                                        type="text"
                                        placeholder="Enter code"
                                        value={couponCode}
                                        onChange={(e) => {
                                            setCouponCode(e.target.value.toUpperCase());
                                            setCouponError('');
                                            setCouponValidation(null);
                                        }}
                                        className="w-full rounded-2xl bg-slate-900 border border-white/10 px-4 py-3 text-white outline-none focus:border-purple-500 uppercase"
                                    />
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (!couponCode.trim()) {
                                                setCouponError('Please enter a coupon code.');
                                                return;
                                            }
                                            setCouponLoading(true);
                                            setCouponError('');
                                            setCouponValidation(null);

                                            const planObj = couponPlan === 'premium' ? plans.premium : plans.starter;
                                            const basePrice = billingPeriod === 'halfYearly' ? planObj.priceHalfYearly[currency] : planObj.priceYearly[currency];
                                            const branches = couponPlan === 'premium' ? couponBranchCount : 1;
                                            const planAmount = basePrice * branches;

                                            try {
                                                const validateRes = await fetch(`${API_URL}/api/payment/validate-coupon`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({ coupon_code: couponCode.trim(), amount: planAmount })
                                                });
                                                const validateData = await validateRes.json();
                                                if (!validateData.success) {
                                                    setCouponError(validateData.message || 'Invalid coupon code.');
                                                } else {
                                                    setCouponValidation(validateData);
                                                    setCouponError('');
                                                    // sync branch count to main state for premium
                                                    if (couponPlan === 'premium') setBranchCount(couponBranchCount);
                                                }
                                            } catch (err) {
                                                setCouponError('Coupon validation failed. Please try again.');
                                            } finally {
                                                setCouponLoading(false);
                                            }
                                        }}
                                        disabled={couponLoading}
                                        className="marketing-btn marketing-btn-secondary px-5 py-3"
                                    >
                                        {couponLoading ? <i className="fas fa-spinner fa-spin"></i> : 'Apply'}
                                    </button>
                                </div>

                                {couponError && (
                                    <div className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                                        <i className="fas fa-times-circle"></i>
                                        <span>{couponError}</span>
                                    </div>
                                )}

                                {couponValidation && (
                                    <div className="rounded-2xl border border-green-500/30 bg-green-500/10 p-5 text-white space-y-2">
                                        <div className="flex items-center gap-2 text-green-400 font-bold text-sm">
                                            <i className="fas fa-tag"></i>
                                            <span>Coupon <strong>{couponValidation.coupon_code}</strong> applied — {couponValidation.discount_percent}% off!</span>
                                        </div>
                                        <div className="flex justify-between text-sm text-gray-300">
                                            <span>Original amount</span>
                                            <span className="line-through text-gray-500">{formatPrice(couponValidation.original_amount)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm text-green-300">
                                            <span>Discount ({couponValidation.discount_percent}%)</span>
                                            <span>- {formatPrice(couponValidation.discount_amount)}</span>
                                        </div>
                                        <div className="flex justify-between text-lg font-black text-white border-t border-white/10 pt-2 mt-1">
                                            <span>Payable amount</span>
                                            <span className="text-green-400">{formatPrice(couponValidation.discounted_amount)}</span>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowCouponInput(false);
                                                const planObj = couponPlan === 'premium' ? plans.premium : plans.starter;
                                                const basePrice = billingPeriod === 'halfYearly' ? planObj.priceHalfYearly[currency] : planObj.priceYearly[currency];
                                                handlePayment(couponPlan, basePrice, couponPlan === 'premium');
                                            }}
                                            disabled={loading}
                                            className="marketing-btn marketing-btn-primary w-full justify-center text-center mt-2"
                                        >
                                            {loading ? <i className="fas fa-spinner fa-spin"></i> : `Pay ${formatPrice(couponValidation.discounted_amount)} →`}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Comparison Section */}
            <section className="marketing-section marketing-section-dark">
                <div className="marketing-container">
                    <h2 className="marketing-section-title text-center mb-12">Detailed Comparison</h2>
                    <div className="marketing-card p-0 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-white/5 border-b border-white/10">
                                        <th className="p-6 text-white font-bold w-1/2">Feature</th>
                                        <th className="p-6 text-center text-gray-300 font-bold w-1/4">Starter</th>
                                        <th className="p-6 text-center text-orange-400 font-bold w-1/4">Premium</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparisonCategories.map((section, sIdx) => (
                                        <React.Fragment key={sIdx}>
                                            <tr className="bg-white/5">
                                                <td colSpan="3" className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                                    {section.category}
                                                </td>
                                            </tr>
                                            {section.items.map((item, iIdx) => (
                                                <tr key={iIdx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                                    <td className="p-4 pl-6 text-gray-300">
                                                        <div className="font-medium text-white">{item.name}</div>
                                                        {item.note && <div className="text-xs text-gray-500 mt-1">{item.note}</div>}
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        {typeof item.starter === 'boolean' ? (
                                                            item.starter ? <i className="fas fa-check text-green-500"></i> : <i className="fas fa-times text-red-500/40"></i>
                                                        ) : (
                                                            <span className="text-sm text-gray-400">{item.starter}</span>
                                                        )}
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        {typeof item.premium === 'boolean' ? (
                                                            item.premium ? <i className="fas fa-check-circle text-orange-500"></i> : <i className="fas fa-times text-red-500/40"></i>
                                                        ) : (
                                                            <span className="text-sm font-bold text-orange-400">{item.premium}</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="marketing-footer">
                <div className="marketing-footer-content">
                    <p>&copy; {new Date().getFullYear()} RedSorm. All rights reserved.</p>
                    <div className="marketing-footer-links">
                        <a href="/terms-of-service" className="marketing-footer-link">Terms of Service</a>
                        <a href="/privacy-policy" className="marketing-footer-link">Privacy Policy</a>
                        <a href="/cookie-policy" className="marketing-footer-link">Cookie Policy</a>
                    </div>
                </div>
            </footer>

            {/* Branch Selection Modal */}
            {showBranchModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
                    <div className="marketing-card max-w-md w-full p-8 border border-white/10 shadow-2xl relative">
                        <button 
                            onClick={() => setShowBranchModal(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white"
                        >
                            <i className="fas fa-times"></i>
                        </button>

                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4 text-purple-400 text-2xl">
                                <i className="fas fa-store"></i>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">How many branches?</h3>
                            <p className="text-gray-400">Premium plan scales with your business.</p>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-gray-400 text-sm font-bold uppercase mb-2 ml-1">Number of Branches</label>
                                <div className="flex items-center gap-4">
                                    <button 
                                        onClick={() => setBranchCount(Math.max(1, branchCount - 1))}
                                        className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 flex items-center justify-center transition-colors"
                                    >
                                        <i className="fas fa-minus"></i>
                                    </button>
                                    <input 
                                        type="number"
                                        min="1"
                                        value={branchCount}
                                        onChange={(e) => setBranchCount(Math.max(1, parseInt(e.target.value) || 1))}
                                        className="flex-1 h-12 bg-white/5 border border-white/10 rounded-xl text-center text-xl font-bold text-white outline-none focus:border-purple-500 transition-colors"
                                    />
                                    <button 
                                        onClick={() => setBranchCount(branchCount + 1)}
                                        className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 flex items-center justify-center transition-colors"
                                    >
                                        <i className="fas fa-plus"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="p-4 bg-white/5 rounded-2xl border border-white/5 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-400">Price per Branch</span>
                                    <span className="text-white font-bold">{formatPrice(pendingPlan?.amount)}</span>
                                </div>
                                <div className="flex justify-between text-lg">
                                    <span className="text-gray-300 font-bold">Total Amount</span>
                                    <span className="text-purple-400 font-black">{formatPrice(pendingPlan?.amount * branchCount)}</span>
                                </div>
                            </div>

                            <button
                                onClick={() => {
                                    setShowBranchModal(false);
                                    handlePayment(pendingPlan.planType, pendingPlan.amount, true);
                                }}
                                className="marketing-btn marketing-btn-primary w-full justify-center text-center py-4 font-black tracking-widest uppercase"
                            >
                                Proceed to Payment
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PricingPage;
